/**
 * Form Style Utilities
 * Constants and helper functions for consistent form styling across the application
 * Ensures compliance with form_styles.md patterns and typography constraints
 */

/**
 * Text Size Constants
 * All non-header text must be 12px (text-xs) or smaller per typography constraint
 */
export const TEXT_BODY = 'text-xs'; // 12px - for body text
export const TEXT_LABEL = 'text-meta'; // 12px - for labels
export const TEXT_MICRO = 'text-micro'; // 11px - for very small text (badges, timestamps)

/**
 * Typography Helper Functions
 */

/**
 * Ensures text size complies with 12px maximum (excluding headers)
 * @param {string} size - Text size class (e.g., 'text-sm', 'text-xs')
 * @returns {string} - Validated text size class (defaults to text-xs if too large)
 */
export const ensureTextSize = (size) => {
  // Allowed sizes (12px or smaller)
  const allowedSizes = ['text-xs', 'text-meta', 'text-micro'];
  
  if (allowedSizes.includes(size)) {
    return size;
  }
  
  // Default to text-xs if size is too large
  console.warn(`Text size ${size} exceeds 12px limit. Using text-xs instead.`);
  return TEXT_BODY;
};

/**
 * Form Container Classes
 */
export const FORM_CONTAINER = 'space-y-6'; // 24px vertical spacing between form sections

/**
 * Page Container Classes
 */
export const PAGE_CONTAINER = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8';

/**
 * Status Message Classes
 */
export const STATUS_SUCCESS = {
  container: 'bg-[var(--ds-status-success-bg)] border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-4 flex items-center justify-between',
  text: 'text-body text-[var(--ds-status-success)] font-medium',
};

export const STATUS_ERROR = {
  container: 'bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4',
  text: 'text-body text-[var(--ds-status-danger)]',
};

export const STATUS_INFO = {
  container: 'bg-[var(--ds-status-info-bg)] border border-[var(--ds-status-info)]/30 rounded-[var(--ds-radius-md)] p-4',
  text: 'text-body text-[var(--ds-status-info)]',
};

/**
 * Action Button Classes (for Add/Remove buttons in tables)
 */
export const ACTION_BUTTON_ADD = {
  base: 'flex items-center justify-end text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded p-1 transition-colors',
};

export const ACTION_BUTTON_REMOVE = {
  base: 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1 transition-colors',
};

/**
 * Helper Text Classes
 */
export const HELPER_TEXT = 'text-xs text-gray-500 dark:text-gray-400';

/**
 * Label Classes
 */
export const LABEL_STANDARD = 'block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1';
export const LABEL_RIGHT_ALIGNED = 'text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right';

/**
 * Input Variant Classes (for reference - use Input component instead)
 */
export const INPUT_STANDARD = 'flex h-9 w-full bg-[var(--ds-control-field-bg)] border border-[var(--ds-control-field-border)] rounded-[var(--ds-radius-md)] px-3 py-2 text-xs text-[var(--ds-text-primary)] placeholder:text-[var(--ds-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:border-[var(--ds-accent-primary)] transition-colors duration-180';

export const INPUT_BORDERLESS = 'flex h-8 w-full bg-transparent border-0 border-b border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-0 text-xs text-gray-900 dark:text-gray-100 transition-colors px-2 py-1';

export const INPUT_TABLE_NUMBER = 'flex w-full bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0 px-0 py-1 no-spinner';

/**
 * Table Classes (for reference - use Table components instead)
 */
export const TABLE_CONTAINER = 'relative pr-8 pl-8';
export const TABLE_HEADER_ROW = 'bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700';
export const TABLE_HEADER_CELL = 'text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider';
export const TABLE_BODY_ROW = 'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30';
export const TABLE_BODY_CELL = 'py-2 px-4 text-xs text-gray-900 dark:text-gray-100';

/**
 * Grid Layout Classes
 */
export const GRID_TWO_COLUMN = 'grid grid-cols-1 md:grid-cols-[45%_20%_35%] gap-0';
export const GRID_NOTES_TOTALS = 'grid grid-cols-1 lg:grid-cols-[68%_30%] gap-6';

/**
 * Card Classes
 */
export const CARD_BASE = 'bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-lg)] shadow-[var(--ds-shadow-card)]';
export const CARD_CONTENT_DEFAULT = 'p-5 pt-4';
export const CARD_CONTENT_FORM = 'p-6'; // Used in forms

/**
 * Helper function to combine classes with typography validation
 * @param {...string} classes - Class names to combine
 * @returns {string} - Combined class string with validated text sizes
 */
export const combineFormClasses = (...classes) => {
  return classes
    .filter(Boolean)
    .map(cls => {
      // Check if class contains text size
      if (typeof cls === 'string' && cls.includes('text-')) {
        // Extract text size class
        const textSizeMatch = cls.match(/text-(xs|sm|base|lg|xl|2xl|3xl|meta|micro|body|h1|h2|display)/);
        if (textSizeMatch) {
          const textSize = textSizeMatch[0];
          // Only validate non-header text sizes
          if (!['text-h1', 'text-h2', 'text-display'].includes(textSize)) {
            return ensureTextSize(textSize);
          }
        }
      }
      return cls;
    })
    .join(' ');
};
